import React, { useState, useRef, useCallback } from 'react';
import { PhotoIcon, XMarkIcon, ArrowUpTrayIcon, TrashIcon, GlobeAltIcon, LinkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface ImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  placeholder?: string;
  accept?: string;
  pageSlug?: string;
  sectionId?: string;
}

type TabType = 'upload' | 'url' | 'library';

// Popular free image sources
const IMAGE_SOURCES = [
  { name: 'Pexels', url: 'https://www.pexels.com', description: 'Free stock photos' },
  { name: 'Unsplash', url: 'https://unsplash.com', description: 'Beautiful free images' },
  { name: 'Pixabay', url: 'https://pixabay.com', description: 'Free images & videos' },
];

// Get API base URL
const getApiBaseUrl = () => {
  // Try different environment variable formats
  const apiUrl = (window as any).__VITE_API_URL__ || 
                 (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || 
                 'http://localhost:3002';
  return apiUrl.replace('/api', '');
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onChange,
  label = 'Image',
  placeholder = 'Enter image URL or upload',
  accept = 'image/*',
  pageSlug,
  sectionId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewError, setPreviewError] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [libraryImages, setLibraryImages] = useState<any[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const apiBaseUrl = getApiBaseUrl();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [pageSlug, sectionId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File is too large. Maximum size is 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setPreviewError(false);

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('altText', file.name);
      formData.append('category', 'cms');
      if (pageSlug) formData.append('pageSlug', pageSlug);
      if (sectionId) formData.append('sectionId', sectionId);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await fetch(`${apiBaseUrl}/api/cms/images/upload`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (data.success && data.data) {
        // Construct full URL
        const imageUrl = data.data.url;
        const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${apiBaseUrl}${imageUrl}`;
        onChange(fullUrl);
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      alert(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExternalUrlSubmit = async () => {
    if (!externalUrl.trim()) {
      setUrlError('Please enter a URL');
      return;
    }

    // Validate URL format
    try {
      new URL(externalUrl);
    } catch {
      setUrlError('Please enter a valid URL');
      return;
    }

    setUrlError('');
    setIsUploading(true);

    try {
      // Save external URL through API (optionally download a copy)
      const response = await fetch(`${apiBaseUrl}/api/cms/images/external`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: externalUrl,
          altText: 'External image',
          category: 'cms',
          pageSlug,
          sectionId,
          downloadCopy: false, // Set to true to download a local copy
        }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        const imageUrl = data.data.url;
        // For external URLs, use the original URL directly
        onChange(imageUrl.startsWith('http') ? imageUrl : externalUrl);
        setExternalUrl('');
      } else {
        // If API fails, just use the URL directly
        onChange(externalUrl);
        setExternalUrl('');
      }
    } catch (error: any) {
      console.error('Failed to save external URL:', error);
      // If API fails, just use the URL directly
      onChange(externalUrl);
      setExternalUrl('');
    } finally {
      setIsUploading(false);
    }
  };

  const loadLibraryImages = async () => {
    setLoadingLibrary(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/cms/images`);
      const data = await response.json();
      if (data.success) {
        setLibraryImages(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load library:', error);
      setLibraryImages([]);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreviewError(false);
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');
    setPreviewError(false);
    setExternalUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageError = () => {
    setPreviewError(true);
  };

  const hasImage = value && value.length > 0;

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'upload'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <ArrowUpTrayIcon className="h-4 w-4 inline mr-1" />
          Upload
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('url')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'url'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <LinkIcon className="h-4 w-4 inline mr-1" />
          URL
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab('library');
            loadLibraryImages();
          }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'library'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <PhotoIcon className="h-4 w-4 inline mr-1" />
          Library
        </button>
      </div>

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer ${
            isDragging
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          <div className="p-8 text-center">
            {isUploading ? (
              <div className="space-y-3">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm font-medium text-gray-600">Uploading...</p>
                <div className="w-48 mx-auto bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            ) : (
              <>
                <ArrowUpTrayIcon className={`h-12 w-12 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className={`text-sm font-medium ${isDragging ? 'text-blue-600' : 'text-gray-600'}`}>
                  {isDragging ? 'Drop image here' : 'Drag & drop an image here'}
                </p>
                <p className="text-xs text-gray-400 mt-1">or click to browse</p>
                <p className="text-xs text-gray-400 mt-2">JPG, PNG, GIF, WebP • Max 10MB</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* URL Tab */}
      {activeTab === 'url' && (
        <div className="space-y-4">
          {/* External URL Input */}
          <div>
            <div className="flex gap-2">
              <input
                type="text"
                value={externalUrl}
                onChange={(e) => {
                  setExternalUrl(e.target.value);
                  setUrlError('');
                }}
                placeholder="https://images.pexels.com/photos/..."
                className={`flex-1 px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  urlError ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              <button
                type="button"
                onClick={handleExternalUrlSubmit}
                disabled={isUploading || !externalUrl.trim()}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isUploading ? 'Adding...' : 'Add'}
              </button>
            </div>
            {urlError && <p className="text-xs text-red-500 mt-1">{urlError}</p>}
          </div>

          {/* Popular Image Sources */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-medium text-gray-500 mb-3">Popular Free Image Sources:</p>
            <div className="flex flex-wrap gap-2">
              {IMAGE_SOURCES.map((source) => (
                <a
                  key={source.name}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-colors"
                >
                  <GlobeAltIcon className="h-4 w-4 mr-1.5" />
                  {source.name}
                </a>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Copy the image URL from these sites and paste above. Right-click on an image → "Copy image address"
            </p>
          </div>
        </div>
      )}

      {/* Library Tab */}
      {activeTab === 'library' && (
        <div>
          {loadingLibrary ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : libraryImages.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <PhotoIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No images in library yet</p>
              <p className="text-xs text-gray-400 mt-1">Upload images to see them here</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 max-h-64 overflow-y-auto p-2">
              {libraryImages.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    const fullUrl = image.url.startsWith('http') ? image.url : `${apiBaseUrl}${image.url}`;
                    onChange(fullUrl);
                  }}
                  className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-colors group"
                >
                  <img
                    src={image.url.startsWith('http') ? image.url : `${apiBaseUrl}${image.url}`}
                    alt={image.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-medium">Select</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Current Image Preview */}
      {hasImage && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500">Current Image</span>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <TrashIcon className="h-3 w-3" />
              Remove
            </button>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
            {!previewError ? (
              <img
                src={value}
                alt="Preview"
                onError={handleImageError}
                className="w-full h-48 object-cover"
              />
            ) : (
              <div className="w-full h-48 flex items-center justify-center">
                <div className="text-center">
                  <PhotoIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-red-500">Could not load image preview</p>
                  <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">{value}</p>
                </div>
              </div>
            )}
          </div>
          {/* URL Display */}
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              value={value}
              onChange={handleUrlChange}
              className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50 font-mono"
              placeholder="Image URL"
            />
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(value)}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
